import React, { useState, useRef, useEffect, useMemo } from "react";
import styled from "styled-components";
import { Button } from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import Loading from "../components/Loading";
import SpendingList from "../components/SpendingList";
import SpendingModal from "../components/SpendingModal";
import UpdateSpendingModal from "../components/UpdateSpendingModal";
import SpendingOutliner from "../components/SpendingOutliner";
import FloatingButton from "../components/FloatingButton";
import useSpendings from "../hooks/useSpendings";
import useSpendingMonths from "../hooks/useSpendingMonths";
import useSpendingDelete from "../hooks/useSpendingDelete";
import getTimezoneOffset from "../utils/timezone";

const SpendingWrapper = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 1.5em 1em 6em;
`;

const AddSpendingBtn = styled(FloatingButton)`
  bottom: 27px;
  right: 18px;
`;

const Sentinel = styled.div`
  height: 1px;
`;

const MonthBanner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0.25em 0.5em;
  padding: 0.6em 0.8em;
  background: #f0f5fa;
  border-radius: 4px;
  color: #274863;
`;

// "YYYY-MM" → 該月本地起訖日期字串，供 spendings 的 startDate/endDate 篩選使用。
function monthToDateRange(month) {
  const start = dayjs(`${month}-01`);
  return {
    startDate: start.format("YYYY-MM-DD"),
    endDate: start.endOf("month").format("YYYY-MM-DD"),
  };
}

export default function Spending() {
  const [activeMonth, setActiveMonth] = useState(null);
  const { months, refetch: refetchMonths } = useSpendingMonths();

  const filters = useMemo(() => {
    if (!activeMonth) {
      return {};
    }

    return { ...monthToDateRange(activeMonth), timezoneOffset: getTimezoneOffset() };
  }, [activeMonth]);

  const { loading, data, loadMore, loadMoreDisabled, queryOption } = useSpendings(30, filters);
  const activeMonthCount = useMemo(
    () => (months.find((entry) => entry.month === activeMonth) || {}).count,
    [months, activeMonth]
  );
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (loading || loadMoreDisabled) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMore();
        }
      },
      { threshold: [0.25] }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [loadMore, loading, loadMoreDisabled]);

  // 增刪改都會改變月份索引（新月份、筆數、清空的月份），完成後更新 outliner。
  const [deleteSpending] = useSpendingDelete(queryOption, refetchMonths);

  const [modalVisible, setModalVisible] = useState(false);

  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [currentSpending, setCurrentSpending] = useState(null);

  const openUpdateModal = (spending) => {
    setCurrentSpending({
      ...spending,
      date: dayjs(Number(spending.date)),
      categoryId: spending.category.id,
      currencyId: spending.currency.id,
    });
    setUpdateModalVisible(true);
  };

  const closeUpdateModal = () => {
    setCurrentSpending(null);
    setUpdateModalVisible(false);
  };

  return (
    <SpendingWrapper>
      <AddSpendingBtn
        onClick={() => setModalVisible(true)}
        type="primary"
        icon={<EditOutlined />}
      />

      <SpendingOutliner months={months} activeMonth={activeMonth} onSelectMonth={setActiveMonth} />

      <SpendingModal
        queryOption={queryOption}
        open={modalVisible}
        onClose={() => setModalVisible(false)}
        onCompleted={refetchMonths}
      />
      {currentSpending && (
        <UpdateSpendingModal
          queryOption={queryOption}
          spending={currentSpending}
          open={updateModalVisible}
          onClose={closeUpdateModal}
          onCompleted={refetchMonths}
        />
      )}

      {activeMonth && (
        <MonthBanner>
          <span>
            檢視 {activeMonth}
            {activeMonthCount !== undefined && `（${activeMonthCount} 筆）`}
          </span>
          <Button type="link" onClick={() => setActiveMonth(null)}>
            回到最新
          </Button>
        </MonthBanner>
      )}

      {data && (
        <SpendingList
          spendings={data.spendings}
          onDelete={(id) => deleteSpending({ variables: { id } })}
          onUpdate={(spending) => openUpdateModal(spending)}
        />
      )}

      {!loadMoreDisabled && <Sentinel ref={sentinelRef} aria-hidden />}

      {loading && <Loading />}
    </SpendingWrapper>
  );
}
